import React, {useEffect, useMemo, useState} from 'react';

import {SortingDirections, ValueFilterHeader} from 'src/components/filter-header';
import {useUserNameMapper} from 'src/components/user-name/hooks/useUserNameMapper';
import {FilterPredicate} from 'src/hooks/filtered-data/filter-predicate';
import {useFilteredData} from 'src/hooks/filtered-data/useFilteredData';
import {SortConfig} from 'src/hooks/useSortingRows';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {ColumnTypes, SortingColumnTypes} from 'src/modules/change-requests/constants';
import {Filters} from 'src/modules/change-requests/typedefs';
import {usersEmailsToUsersNames} from 'src/modules/change-requests/utils/usersEmailsToUsersNames';
import {usersNamesToUsersEmails} from 'src/modules/change-requests/utils/usersNamesToUsersEmails';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';

type Props = {
    rows: ChangeRequest[],
    filters: Filters,
    setFilter: (filterType: string, filterValue: string[]) => void,
    filterPredicates: (filters: Filters) => FilterPredicate<ChangeRequest>[],
    filteringColumnType: ColumnTypes,
    sortingColumnType: SortingColumnTypes,
    onSort: (config: SortConfig<SortingColumnTypes>) => void,
    sortConfig: SortConfig<SortingColumnTypes>,
    columnDataHandler: (filteredRows: ChangeRequest[], columnType: ColumnTypes) => string[],
    caption: string,
    onlyMyFilter: boolean,
};

export const AuthorColumnHeader = (props: Props) => {
    const {
        rows,
        filters,
        setFilter,
        filterPredicates,
        onSort,
        sortConfig,
        filteringColumnType,
        sortingColumnType,
        columnDataHandler,
        caption,
        onlyMyFilter,
    } = props;

    const userEmail = useUserEmail();
    const filtersExcludingCurrentColumn = {...filters, [filteringColumnType]: []};
    const filteredRows = useFilteredData(rows, filtersExcludingCurrentColumn, filterPredicates);
    const [columnData, setColumnData] = useState<Array<string>>([]);
    const {emailToName, nameToEmail} = useUserNameMapper();
    const [isOnlyMyFilterActive, setIsOnlyMyFilterActive] = useState(false);

    const authorsNames = useMemo(() => {
        return usersEmailsToUsersNames(filters[filteringColumnType] as string[], emailToName);
    }, [filters, filteringColumnType, emailToName]);

    useEffect(() => {
        if (onlyMyFilter) {
            setFilter(filteringColumnType, [userEmail]);
        }

        if (isOnlyMyFilterActive && !onlyMyFilter) {
            setFilter(filteringColumnType, []);
        }

        setIsOnlyMyFilterActive(onlyMyFilter);
    }, [filteringColumnType, isOnlyMyFilterActive, onlyMyFilter, setFilter, userEmail]);

    useEffect(() => {
        if (onlyMyFilter) {
            setColumnData(usersEmailsToUsersNames([userEmail], emailToName));
            return;
        }

        const authorsEmails = columnDataHandler(filteredRows, filteringColumnType);
        setColumnData(usersEmailsToUsersNames(authorsEmails, emailToName));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteringColumnType, columnDataHandler, onlyMyFilter, emailToName, userEmail, filters]);

    const filterHandler = (filterValues: string[]) => {
        setFilter(filteringColumnType, usersNamesToUsersEmails(filterValues, nameToEmail));
    };

    const sortHandler = (direction: SortingDirections) => {
        if (onSort) {
            onSort({key: sortingColumnType, direction});
        }
    };

    return (
        <ValueFilterHeader
            caption={caption}
            columnData={columnData}
            setFilter={filterHandler}
            filters={authorsNames}
            isSorted={sortConfig.key === sortingColumnType}
            onSort={sortHandler}
        />
    );
};
